import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { pick } from '../../util/common';
import { syncDeferredWallet, withdrawDeferredWallet } from '../../util/api';
import {
  createStripeAccount,
  updateStripeAccount,
  fetchStripeAccount,
} from '../../ducks/stripeConnectAccount.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';

// ================ Async thunks ================ //

const savePayoutDetailsPayloadCreator = (
  { values, isUpdateCall },
  { dispatch, extra: sdk, rejectWithValue }
) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;

  return dispatch(upsertThunk(values, { expand: true }))
    .then(response => {
      return response;
    })
    .catch(() => {
      return rejectWithValue('Failed to save payout details');
    });
};
export const savePayoutDetailsThunk = createAsyncThunk(
  'StripePayoutPage/savePayoutDetails',
  savePayoutDetailsPayloadCreator
);
// Backward compatible wrapper function
export const savePayoutDetails = (values, isUpdateCall) => dispatch => {
  return dispatch(savePayoutDetailsThunk({ values, isUpdateCall })).unwrap();
};

const withdrawDeferredWalletPayloadCreator = (_, { dispatch, rejectWithValue }) => {
  return withdrawDeferredWallet({})
    .then(response => {
      return dispatch(
        fetchCurrentUser({
          updateHasListings: false,
          updateNotifications: false,
          enforce: true,
        })
      ).then(() => response);
    })
    .catch(e => rejectWithValue(e));
};

export const withdrawDeferredWalletThunk = createAsyncThunk(
  'StripePayoutPage/withdrawDeferredWallet',
  withdrawDeferredWalletPayloadCreator
);

export const withdrawWalletBalance = () => dispatch => {
  return dispatch(withdrawDeferredWalletThunk()).unwrap();
};

const syncDeferredWalletPayloadCreator = (_, { dispatch, rejectWithValue }) => {
  return syncDeferredWallet({})
    .then(response => {
      return dispatch(
        fetchCurrentUser({
          updateHasListings: false,
          updateNotifications: false,
          enforce: true,
        })
      ).then(() => response);
    })
    .catch(e => rejectWithValue(e));
};

export const syncDeferredWalletThunk = createAsyncThunk(
  'StripePayoutPage/syncDeferredWallet',
  syncDeferredWalletPayloadCreator
);

export const syncDeferredWalletForCurrentUser = () => dispatch => {
  return dispatch(syncDeferredWalletThunk()).unwrap();
};

// ================ Slice ================ //

const initialState = {
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  withdrawInProgress: false,
  withdrawError: null,
  withdrawSucceeded: false,
  syncWalletInProgress: false,
  syncWalletError: null,
  fromReturnURL: false,
};

const stripePayoutPageSlice = createSlice({
  name: 'StripePayoutPage',
  initialState,
  reducers: {
    setInitialValues: (state, action) => {
      return { ...initialState, ...pick(action.payload, Object.keys(initialState)) };
    },
  },
  extraReducers: builder => {
    builder
      // Save Payout Details cases
      .addCase(savePayoutDetailsThunk.pending, state => {
        state.payoutDetailsSaveInProgress = true;
      })
      .addCase(savePayoutDetailsThunk.fulfilled, state => {
        state.payoutDetailsSaveInProgress = false;
        state.payoutDetailsSaved = true;
      })
      .addCase(savePayoutDetailsThunk.rejected, state => {
        state.payoutDetailsSaveInProgress = false;
      })
      .addCase(withdrawDeferredWalletThunk.pending, state => {
        state.withdrawInProgress = true;
        state.withdrawError = null;
        state.withdrawSucceeded = false;
      })
      .addCase(withdrawDeferredWalletThunk.fulfilled, state => {
        state.withdrawInProgress = false;
        state.withdrawSucceeded = true;
      })
      .addCase(withdrawDeferredWalletThunk.rejected, (state, action) => {
        state.withdrawInProgress = false;
        state.withdrawError = action.payload || action.error;
      })
      .addCase(syncDeferredWalletThunk.pending, state => {
        state.syncWalletInProgress = true;
        state.syncWalletError = null;
      })
      .addCase(syncDeferredWalletThunk.fulfilled, state => {
        state.syncWalletInProgress = false;
      })
      .addCase(syncDeferredWalletThunk.rejected, (state, action) => {
        state.syncWalletInProgress = false;
        state.syncWalletError = action.payload || action.error;
      });
  },
});

// Export the action creators
export const { setInitialValues } = stripePayoutPageSlice.actions;

export default stripePayoutPageSlice.reducer;

// ================ Load Data ================ //

export const loadData = () => (dispatch, getState, sdk) => {
  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialValues());
  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };

  return dispatch(fetchCurrentUser(fetchCurrentUserOptions)).then(response => {
    const currentUser = getState().user.currentUser;
    const syncWallet = currentUser
      ? dispatch(syncDeferredWalletForCurrentUser()).catch(() => null)
      : Promise.resolve(null);

    return syncWallet.then(() => {
      const updatedCurrentUser = getState().user.currentUser;
      if (updatedCurrentUser && updatedCurrentUser.stripeAccount) {
        dispatch(fetchStripeAccount());
      }
      return response;
    });
  });
};
